import {getSiteUrl} from "@/helpers/functions";

export interface PodcastGuest {
    name: string;
    url?: string;
}

export interface SeoSchemaPodcastEpisodeProps {
    slug: string;
    title: string;
    description: string;
    link: string;
    featuredImage?: string | string[];
    datePublished: string;
    dateModified?: string;
    seriesName?: string;
    seriesUrl?: string;
    audioUrl?: string;
    embedUrl?: string;
    duration?: string;
    guest?: PodcastGuest;
    episodeNumber?: number;
    seasonNumber?: number;
}

const toIso = (value: string): string => {
    if (!value) return "";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toISOString();
};

const toAbsoluteUrl = (siteUrl: string, url: string): string => {
    if (/^https?:\/\//i.test(url)) return url;
    const path = url.startsWith("/") ? url : `/${url}`;
    return `${siteUrl}${path}`;
};

export function SeoSchemaPodcastEpisode({
    slug,
    title,
    description,
    link,
    featuredImage,
    datePublished,
    dateModified,
    seriesName,
    seriesUrl,
    audioUrl,
    embedUrl,
    duration,
    guest,
    episodeNumber,
    seasonNumber,
}: SeoSchemaPodcastEpisodeProps) {
    const name = title?.trim() ?? "";
    const trimmedDescription = description?.trim() ?? "";
    const trimmedSeries = seriesName?.trim() ?? "";

    if (!name) {
        console.error(`[SeoSchemaPodcastEpisode] HALT: Podcast "${slug}" is missing a title. Populate the post title in the CMS. Skipping PodcastEpisode JSON-LD.`);
        return null;
    }
    if (!trimmedDescription) {
        console.error(`[SeoSchemaPodcastEpisode] HALT: Podcast "${slug}" is missing a description. Populate annotation (or seoDescription) in the CMS. Skipping PodcastEpisode JSON-LD.`);
        return null;
    }
    if (!trimmedSeries) {
        console.error(`[SeoSchemaPodcastEpisode] HALT: Podcast "${slug}" is missing partOfSeries.name. Add "podcastSeriesName" to the CMS post. Skipping PodcastEpisode JSON-LD.`);
        return null;
    }

    const siteUrl = getSiteUrl();
    const absoluteLink = toAbsoluteUrl(siteUrl, link);

    const images = (Array.isArray(featuredImage) ? featuredImage : [featuredImage])
        .filter((img): img is string => Boolean(img))
        .map(img => toAbsoluteUrl(siteUrl, img));

    const partOfSeries: Record<string, unknown> = {
        "@type": "PodcastSeries",
        "name": trimmedSeries,
    };
    if (seriesUrl) partOfSeries.url = toAbsoluteUrl(siteUrl, seriesUrl);

    const published = toIso(datePublished);
    const modified = toIso(dateModified || datePublished);

    const schema: Record<string, unknown> = {
        "@context": "https://schema.org",
        "@type": "PodcastEpisode",
        "@id": absoluteLink,
        "mainEntityOfPage": absoluteLink,
        "name": name,
        "url": absoluteLink,
        "description": trimmedDescription,
        "partOfSeries": partOfSeries,
        "publisher": {"@id": `${siteUrl}/#organization`},
    };

    if (images.length) schema.image = images;
    if (published) schema.datePublished = published;
    if (modified) schema.dateModified = modified;
    if (duration) schema.timeRequired = duration;
    if (typeof episodeNumber === "number") schema.episodeNumber = episodeNumber;
    if (typeof seasonNumber === "number") schema.seasonNumber = seasonNumber;

    if (audioUrl || embedUrl) {
        const mediaEntries: Array<Record<string, unknown>> = [];
        if (audioUrl) {
            const media: Record<string, unknown> = {
                "@type": "MediaObject",
                "contentUrl": audioUrl,
            };
            if (duration) media.duration = duration;
            mediaEntries.push(media);
        }
        if (embedUrl) {
            const media: Record<string, unknown> = {
                "@type": "MediaObject",
                "embedUrl": embedUrl,
            };
            if (duration) media.duration = duration;
            mediaEntries.push(media);
        }
        schema.associatedMedia = mediaEntries.length === 1 ? mediaEntries[0] : mediaEntries;
    }

    if (guest?.name) {
        schema.actor = {
            "@type": "Person",
            "name": guest.name,
            ...(guest.url ? {url: toAbsoluteUrl(siteUrl, guest.url)} : {}),
        };
    }

    return (
        <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
        />
    );
}
